import React, { useEffect, useState } from 'react';

import BarraInferiorBotoes from '../../../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../../../components/BoxConteudo';
import CustomCol from '../../../../../../../components/CustomCol';
import CustomInputInt from '../../../../../../../components/CustomInputs/CustomInputInt';
import CustomInputText from '../../../../../../../components/CustomInputs/CustomInputText';
import CustomOffcanvas from '../../../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../../../components/CustomRow';
import Log from '../../../../../../../components/Log/Log';
import WorkspaceBotoes from '../../../../../../../components/WorkspaceBotoes';
import { IFluxoAdminEtapaForm } from '../../../../../../../modules/types/FluxoAdmin';
import { formataNomeMeioSiglas } from '../../../../../../../utils/functions';
import { handleDelete } from '../../../../../../../utils/handleDelete';
import { handleSave } from '../../../../../../../utils/handleSave';
import { swalAttention } from '../../../../../../../utils/swal';

interface Props {
  modeloId: number;
  registro?: any;
  onClose: () => void;
  onSaved: () => void;
  onLogsRefresh?: () => void;
  existingOrders?: number[];
}

const defaultForm: IFluxoAdminEtapaForm = {
  id: 0,
  modeloId: 0,
  ordem: 1,
  areaResponsavel: '',
  estimativaDias: 0,
  etapa: '',
  descricao: '',
  observacao: '',
};

const FluxoAdminEtapaForm: React.FC<Props> = ({
  modeloId,
  registro,
  onClose,
  onSaved,
  onLogsRefresh,
  existingOrders = [],
}) => {
  const nextOrder =
    existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

  const [form, setForm] = useState<IFluxoAdminEtapaForm>({
    ...defaultForm,
    modeloId,
    ordem: nextOrder,
  });
  const [logRefresh, setLogRefresh] = useState(0);

  useEffect(() => {
    if (registro) {
      setForm({
        id: registro.id,
        modeloId: registro.modeloId || modeloId,
        ordem: registro.ordem,
        areaResponsavel: registro.areaResponsavel,
        estimativaDias: registro.estimativaDias,
        etapa: registro.etapa,
        descricao: registro.descricao || '',
        observacao: registro.observacao || '',
      });
      return;
    }

    setForm({
      ...defaultForm,
      modeloId,
      ordem: nextOrder,
    });
  }, [registro, modeloId, nextOrder]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    const parsed =
      name === 'ordem' || name === 'estimativaDias' ? Number(value) : value;
    setForm((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleBlurOrder = (
    event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;

    if (name === 'ordem') {
      const ordemValue = Number(value);
      if (existingOrders.includes(ordemValue)) {
        swalAttention(
          `Ordem Nº <span style="font-weight:bold; background-color:yellow; padding:5px 15px; border-radius:4px; color:black">${ordemValue}</span> já existe.<br> Por favor, escolha um valor diferente.`,
        );

        setForm((prev) => ({ ...prev, ordem: 0 }));
      }
    }
  };

  const camposObrigatorios = {
    ordem: 'Ordem',
    areaResponsavel: 'Área Responsável',
    estimativaDias: 'Estimativa (dias)',
    etapa: 'Etapa',
  };

  const camposAjustar = {
    number: ['id', 'modeloId', 'ordem', 'estimativaDias'],
    string: ['areaResponsavel', 'etapa', 'descricao', 'observacao'],
  };

  const handleSubmit = async () => {
    const ordemValue = Number(form.ordem);
    if (existingOrders.includes(ordemValue)) {
      swalAttention(
        `Ordem Nº <span style="font-weight:bold; background-color:yellow; padding:5px 15px; border-radius:4px; color:black">${ordemValue}</span> já existe.<br> Por favor, escolha um valor diferente.`,
      );
      return;
    }

    const saved = await handleSave(
      { ...form, modeloId },
      camposObrigatorios,
      camposAjustar,
      'fluxoadmetapas',
      'daf/adm/fluxos-adm/modelo',
      undefined,
      false,
      registro,
    );

    if (saved) {
      // Atualiza dados mas mantém o offcanvas aberto
      onSaved();
      onLogsRefresh?.();
      setLogRefresh((prev) => prev + 1);
    }
  };

  const handleDeleteWrapper = async () => {
    const deleted = await handleDelete(form.id, 'fluxoadmetapas');
    if (!deleted) return;

    onLogsRefresh?.();
    onSaved();
    onClose();
  };

  return (
    <CustomOffcanvas
      show
      onClose={onClose}
      title={form.id ? 'Editar Etapa' : 'Nova Etapa'}
      width="55%"
      showOpenButton={false}
    >
      {registro && registro.id !== 0 && (
        <div style={{ marginBottom: '12px' }}>
          <Log
            id={registro.id}
            dataRegistro={registro.dataRegistro}
            usuarioRegistro={formataNomeMeioSiglas(
              registro.usuarioRegistro?.nomeCompleto,
            )}
            dataAtualizacao={registro.dataUltimaAtualizacao}
            usuarioAtualizacao={formataNomeMeioSiglas(
              registro.usuarioAtualizacao?.nomeCompleto,
            )}
            nEdicoes={registro.nEdicoes}
            entity="TB_FLUXO_ADM_ETAPA"
            refreshTriggerLog={logRefresh}
          />
        </div>
      )}

      <BoxConteudo boxTitulo="Etapa">
        <CustomRow>
          <CustomCol width="120px">
            <CustomInputInt
              name="ordem"
              title="Ordem"
              value={form.ordem}
              onChange={handleChange}
              onBlur={handleBlurOrder}
              minValue={1}
              required
            />
          </CustomCol>

          <CustomCol width="180px">
            <CustomInputInt
              name="estimativaDias"
              title="Estimativa (dias)"
              value={form.estimativaDias}
              onChange={handleChange}
              minValue={0}
              required
            />
          </CustomCol>

          <CustomCol width="220px">
            <CustomInputText
              name="areaResponsavel"
              title="Área Responsável"
              value={form.areaResponsavel}
              onChange={handleChange}
              maxLength={20}
              required
            />
          </CustomCol>
          <CustomCol />
        </CustomRow>

        <CustomRow>
          <CustomCol width="100%">
            <CustomInputText
              name="etapa"
              title="Etapa"
              value={form.etapa}
              onChange={handleChange}
              maxLength={120}
              required
            />
          </CustomCol>
        </CustomRow>

        <CustomRow>
          <CustomCol width="100%">
            <CustomInputText
              name="descricao"
              title="Descrição"
              value={form.descricao}
              onChange={handleChange}
              type="textArea"
              maxLength={500}
            />
          </CustomCol>
        </CustomRow>

        <CustomRow>
          <CustomCol width="100%">
            <CustomInputText
              name="observacao"
              title="Observação"
              type="textArea"
              value={form.observacao}
              onChange={handleChange}
              maxLength={255}
            />
          </CustomCol>
        </CustomRow>
      </BoxConteudo>
      <BarraInferiorBotoes>
        <WorkspaceBotoes
          cancelar={false}
          delete={form.id !== 0}
          onClickDelete={handleDeleteWrapper}
          save
          onClickSave={handleSubmit}
        />
      </BarraInferiorBotoes>
    </CustomOffcanvas>
  );
};

export default FluxoAdminEtapaForm;
